import { ref, type Ref } from 'vue'
import type {
  CareOrderListItem,
  CareOrderMetaData,
  OrderCardDTO,
} from '@/api/orders'
import { dedupePlaceholderFollowUpCareLines, isLikelyCareOrderBusinessNumber, mergeCareOrderCardPreservingLocalFields, readCareOrderNumericId } from '@/api/orders'
import {
  normalizeOrderCardForDisplay,
  preserveMainLineOnMergedFollowUpCard,
  promoteBookedScheduleOnPendingCard,
} from '@/api/careOrderLineDisplay'
import { isFollowUpCareLine } from '@/api/careOrderLineDisplay'
import type { TechnicianNoLookup } from '@/utils/technicianNoFromList'
import type { CareScheduleFocusPayload, UnscheduledItem } from '../types'

export interface UseCareScheduleOrdersParams {
  focusPayload: Ref<CareScheduleFocusPayload | null>
  scheduleClearedByUser: Ref<Set<string>>
  unscheduled: Ref<UnscheduledItem[]>
  assignedOrdersCache: Ref<OrderCardDTO[]>
  lineIndexForScheduleCard: (o: OrderCardDTO, focusOrderId: string, focusLineIndex: number) => number
  ymdFromDate: (d: Date) => string
  parseServiceCycleDate: (raw: string) => Date | null
  resolveServiceCycleDateFromOrderCard: (o: OrderCardDTO, lineIndex: number) => Date | null
  scheduleTargetLineIndexForOrder: (orderId: string) => number
}

export function useCareScheduleOrders(params: UseCareScheduleOrdersParams) {
  const {
    focusPayload,
    scheduleClearedByUser,
    unscheduled,
    assignedOrdersCache,
    lineIndexForScheduleCard,
    ymdFromDate,
    parseServiceCycleDate,
    resolveServiceCycleDateFromOrderCard,
    scheduleTargetLineIndexForOrder,
  } = params

  const ordersById = ref<Record<string, OrderCardDTO>>({})
  const serviceCycleByOrderId = ref<Record<string, string>>({})
  const careMeta = ref<CareOrderMetaData | null>(null)
  const durationLabelsByOrderId = ref<Record<string, string>>({})
  const planLabelsByOrderId = ref<Record<string, string>>({})
  const technicianNoLookup = ref<TechnicianNoLookup | null>(null)

  function orderCardHasMainAndFollowUpLines(order: OrderCardDTO): boolean {
    return (
      order.services.some((l) => isFollowUpCareLine(l)) &&
      order.services.some((l) => !isFollowUpCareLine(l))
    )
  }

  function careOrderCardsSameOrder(a: OrderCardDTO, b: OrderCardDTO): boolean {
    const idA = (a.id ?? '').trim()
    const idB = (b.id ?? '').trim()
    if (idA && idB && idA === idB) return true
    const pkA = a.orderPrimaryId
    const pkB = b.orderPrimaryId
    if (pkA == null || pkB == null || pkA !== pkB) return false
    /** 业务单号与短数字展示 id 不同：视为列表中两张独立卡片，避免误合并 */
    if (idA && idB && idA !== idB) {
      const numA = readCareOrderNumericId(idA)
      const numB = readCareOrderNumericId(idB)
      if (
        (numA != null && isLikelyCareOrderBusinessNumber(numA)) ||
        (numB != null && isLikelyCareOrderBusinessNumber(numB))
      ) {
        return false
      }
    }
    return true
  }

  function parseOrderPrimaryKeyFromId(raw: string): number | null {
    const t = raw.trim()
    if (!t) return null
    const hm = t.match(/^HM-(\d+)$/i)
    if (hm?.[1]) {
      const n = parseInt(hm[1], 10)
      return Number.isFinite(n) && !isLikelyCareOrderBusinessNumber(n) ? n : null
    }
    if (/^\d+$/.test(t)) {
      const n = parseInt(t, 10)
      return Number.isFinite(n) && !isLikelyCareOrderBusinessNumber(n) ? n : null
    }
    return null
  }

  function orderIdMatchesCard(orderId: string, card: OrderCardDTO): boolean {
    const id = orderId.trim()
    if (!id) return false
    const cardId = (card.id ?? '').trim()
    if (cardId && cardId === id) return true
    const pk = card.orderPrimaryId
    if (pk == null) return false
    const pkStr = String(Math.trunc(pk))
    if (id === pkStr || id === `HM-${pkStr}`) {
      const cardIdNum = readCareOrderNumericId(cardId)
      if (cardIdNum != null && isLikelyCareOrderBusinessNumber(cardIdNum) && id !== cardId) {
        return false
      }
      return true
    }
    const idPk = parseOrderPrimaryKeyFromId(id)
    if (idPk != null && idPk === Math.trunc(pk)) {
      const cardIdNum = readCareOrderNumericId(cardId)
      if (cardIdNum != null && isLikelyCareOrderBusinessNumber(cardIdNum) && id !== cardId) {
        return false
      }
      return true
    }
    return false
  }

  function findOrderByExactDisplayId(orderId: string): OrderCardDTO | undefined {
    const id = orderId.trim()
    if (!id) return undefined
    const direct = ordersById.value[id]
    if (direct && (direct.id ?? '').trim() === id) return direct
    const fromMap = Object.values(ordersById.value).find((o) => (o.id ?? '').trim() === id)
    if (fromMap) return fromMap
    return focusPayload.value?.orderList?.find((o) => (o.id ?? '').trim() === id)
  }

  function findOrder(orderId: string): OrderCardDTO | undefined {
    const id = orderId.trim()
    if (!id) return undefined
    const exact = findOrderByExactDisplayId(id)
    if (exact) return exact
    const needle = { id } as OrderCardDTO
    const fromMap = Object.values(ordersById.value).find(
      (o) => careOrderCardsSameOrder(o, needle) || orderIdMatchesCard(id, o),
    )
    if (fromMap) return fromMap
    const fromFocus = focusPayload.value?.orderList?.find(
      (o) => careOrderCardsSameOrder(o, needle) || orderIdMatchesCard(id, o),
    )
    if (fromFocus) return fromFocus
    return assignedOrdersCache.value.find(
      (o) => careOrderCardsSameOrder(o, needle) || orderIdMatchesCard(id, o),
    )
  }

  function careOrderIdsReferToSameOrder(aOrderId: string, bOrderId: string): boolean {
    const aId = aOrderId.trim()
    const bId = bOrderId.trim()
    if (aId && bId && aId === bId) return true
    if (aId && bId && aId !== bId) {
      const numA = readCareOrderNumericId(aId)
      const numB = readCareOrderNumericId(bId)
      if (
        numA != null &&
        numB != null &&
        isLikelyCareOrderBusinessNumber(numA) &&
        isLikelyCareOrderBusinessNumber(numB) &&
        numA !== numB
      ) {
        return false
      }
    }
    const a = findOrderByExactDisplayId(aOrderId) ?? findOrder(aOrderId)
    const b = findOrderByExactDisplayId(bOrderId) ?? findOrder(bOrderId)
    if (a && b && careOrderCardsSameOrder(a, b)) return true
    if (a && orderIdMatchesCard(bOrderId, a)) return true
    if (b && orderIdMatchesCard(aOrderId, b)) return true
    const aPk = parseOrderPrimaryKeyFromId(aOrderId)
    const bPk = parseOrderPrimaryKeyFromId(bOrderId)
    if (aPk != null && bPk != null && aPk === bPk) return true
    if (aPk != null && b?.orderPrimaryId != null && aPk === Math.trunc(b.orderPrimaryId)) {
      const bId = (b.id ?? '').trim()
      const bIdNum = readCareOrderNumericId(bId)
      if (aId !== bId && bIdNum != null && isLikelyCareOrderBusinessNumber(bIdNum)) return false
      return true
    }
    if (bPk != null && a?.orderPrimaryId != null && bPk === Math.trunc(a.orderPrimaryId)) {
      const aIdStr = (a.id ?? '').trim()
      const aIdNum = readCareOrderNumericId(aIdStr)
      if (bId !== aIdStr && aIdNum != null && isLikelyCareOrderBusinessNumber(aIdNum)) return false
      return true
    }
    return false
  }

  function resolveCanonicalOrderId(orderId: string): string {
    const id = orderId.trim()
    if (!id) return ''
    const focusId = focusPayload.value?.orderId?.trim()
    if (focusId && id === focusId) return focusId
    if (focusId && careOrderIdsReferToSameOrder(id, focusId)) return focusId
    const exact = findOrderByExactDisplayId(id)
    if (exact) return (exact.id ?? id).trim()
    return findOrder(orderId)?.id?.trim() || id
  }

  function isOrderScheduleCleared(orderId: string): boolean {
    const id = orderId.trim()
    if (!id) return false
    if (scheduleClearedByUser.value.has(id)) return true
    const order = findOrder(id)
    if (!order) return false
    for (const clearedId of scheduleClearedByUser.value) {
      const cleared = findOrder(clearedId) ?? ({ id: clearedId } as OrderCardDTO)
      if (careOrderCardsSameOrder(order, cleared)) return true
    }
    return false
  }

  function cacheServiceCycleForOrder(o: OrderCardDTO, lineIndex?: number) {
    const focus = focusPayload.value
    const idx =
      lineIndex ??
      lineIndexForScheduleCard(o, focus?.orderId ?? o.id, focus?.lineIndex ?? 0)
    const resolved = resolveServiceCycleDateFromOrderCard(o, idx)
    if (resolved) {
      serviceCycleByOrderId.value = { ...serviceCycleByOrderId.value, [o.id]: ymdFromDate(resolved) }
      return
    }
    const raw = o.serviceDate?.trim()
    const m = raw?.match(/(\d{4}-\d{2}-\d{2})/)
    if (m) {
      serviceCycleByOrderId.value = { ...serviceCycleByOrderId.value, [o.id]: m[1]! }
    }
  }

  function registerOrderCards(orders: OrderCardDTO[]) {
    if (!orders.length) return
    const next = { ...ordersById.value }
    for (const o of orders) {
      const existingEntry = Object.entries(next).find(([, val]) => careOrderCardsSameOrder(val, o))
      const existingKey = existingEntry?.[0]
      const canonicalId = ((existingEntry?.[1]?.id ?? o.id) || o.id).trim()
      if (!canonicalId) continue
      const focusId = focusPayload.value?.orderId?.trim()
      const isClearedFocusOrder =
        isOrderScheduleCleared(canonicalId) &&
        !!focusId &&
        careOrderIdsReferToSameOrder(canonicalId, focusId)
      if (isOrderScheduleCleared(canonicalId) && !isClearedFocusOrder) continue
      if (isClearedFocusOrder) {
        const prev = next[canonicalId] ?? existingEntry?.[1]
        if (prev) {
          if (existingKey && existingKey !== canonicalId) delete next[existingKey]
          next[canonicalId] = prev
          cacheServiceCycleForOrder(prev)
          continue
        }
      }
      if (existingKey && existingKey !== canonicalId) delete next[existingKey]
      const incoming = normalizeOrderCardForDisplay({ ...o, id: canonicalId }) ?? { ...o, id: canonicalId }
      const prev = next[canonicalId] ?? existingEntry?.[1]
      let merged = prev ? mergeCareOrderCardPreservingLocalFields(prev, incoming) : incoming
      merged = normalizeOrderCardForDisplay(merged) ?? merged
      if (prev && orderCardHasMainAndFollowUpLines(prev) && orderCardHasMainAndFollowUpLines(merged)) {
        merged = preserveMainLineOnMergedFollowUpCard(prev, merged)
      }
      merged = {
        ...merged,
        services: dedupePlaceholderFollowUpCareLines(merged.services),
      }
      next[canonicalId] = merged
      cacheServiceCycleForOrder(next[canonicalId]!)
    }
    ordersById.value = next
  }

  /** Unassign 后写入本地 Pending 卡片，绕过 registerOrderCards 对 cleared 单的跳过 */
  function upsertLocalFocusOrderCard(order: OrderCardDTO): OrderCardDTO {
    const canonicalId = (order.id ?? '').trim()
    if (!canonicalId) return order
    const focus = focusPayload.value
    const listId =
      focus?.orderList?.find((o) => careOrderCardsSameOrder(o, order))?.id.trim() ||
      focus?.orderId?.trim() ||
      canonicalId
    const card: OrderCardDTO = { ...order, id: listId }
    const next = purgeDuplicateOrdersById(card, listId)
    next[listId] = card
    ordersById.value = next
    if (focus) {
      focus.orderList = [card]
      focusPayload.value = { ...focus, orderList: [card] }
    }
    upsertFocusOrderList(listId, card)
    return card
  }

  function upsertFocusOrderList(orderId: string, nextOrder: OrderCardDTO): void {
    const focus = focusPayload.value
    if (!focus?.orderList?.length) return
    const canonicalId = orderId.trim()
    const idx = focus.orderList.findIndex(
      (o) => o.id === canonicalId || careOrderCardsSameOrder(o, nextOrder),
    )
    if (idx < 0) return
    const listId = focus.orderList[idx]!.id.trim() || canonicalId
    focus.orderList = focus.orderList.map((o, i) =>
      i === idx ? { ...nextOrder, id: listId } : o,
    )
  }

  function purgeDuplicateOrdersById(nextOrder: OrderCardDTO, canonicalId: string): Record<string, OrderCardDTO> {
    const next = { ...ordersById.value }
    for (const [key, val] of Object.entries(next)) {
      if (key === canonicalId) continue
      if (careOrderCardsSameOrder(val, nextOrder)) delete next[key]
    }
    return next
  }

  function serviceCycleYmdForOrderId(orderId: string): string | undefined {
    const cached = serviceCycleByOrderId.value[orderId]?.trim()
    if (cached) return cached
    const o = findOrder(orderId)
    return o?.serviceDate?.trim().match(/(\d{4}-\d{2}-\d{2})/)?.[1]
  }

  function durationLabelForOrderId(orderId: string): string {
    const item = unscheduled.value.find((x) => x.orderId === orderId)
    if (item?.durationLabel.trim()) return item.durationLabel.trim()
    return (durationLabelsByOrderId.value[orderId] ?? '').trim()
  }

  function resolvePlanDisplayName(planKey: string | undefined): string {
    const key = (planKey ?? '').trim()
    if (!key) return ''
    const plans = careMeta.value?.plans ?? []
    const normalized = key.replace(/-/g, '_').toLowerCase()
    const hit = plans.find((p) => {
      const v = String(p.value ?? '').replace(/-/g, '_').toLowerCase()
      return v === normalized
    })
    return hit?.label?.trim() || ''
  }

  function planDisplayNameForOrder(orderId: string, fallback = ''): string {
    return planLabelsByOrderId.value[orderId]?.trim() || fallback.trim()
  }

  function cachePlanLabelFromDetail(orderId: string, detail: CareOrderListItem) {
    const planLabel = resolvePlanDisplayName(detail.plan_key)
    if (!planLabel) return
    planLabelsByOrderId.value = { ...planLabelsByOrderId.value, [orderId]: planLabel }
  }

  function preserveNonTargetLinesScheduleOnBackendMerge(
    local: OrderCardDTO | undefined,
    merged: OrderCardDTO,
    orderId: string,
  ): OrderCardDTO {
    if (!local?.services.length) return merged
    const localMainIdx = local.services.findIndex((l) => !isFollowUpCareLine(l))
    const localFuIdx = local.services.findIndex((l) => isFollowUpCareLine(l))
    if (localMainIdx < 0) return merged

    const focus = focusPayload.value
    const targetLineIndex =
      focus?.orderId === orderId
        ? (focus.lineIndex ?? scheduleTargetLineIndexForOrder(orderId))
        : scheduleTargetLineIndexForOrder(orderId)
    const targetLine = merged.services[targetLineIndex]
    const targetingFollowUp =
      (targetLine != null && isFollowUpCareLine(targetLine)) ||
      (localFuIdx >= 0 && targetLineIndex === localFuIdx)
    if (!targetingFollowUp) return merged

    return preserveMainLineOnMergedFollowUpCard(local, merged)
  }

  function mergeOrderCardFromBackend(orderId: string, card: OrderCardDTO): OrderCardDTO {
    const canonicalId = orderId.trim()
    const local = findOrder(canonicalId)
    const listId = (local?.id ?? canonicalId).trim() || card.id
    let merged: OrderCardDTO = local
      ? mergeCareOrderCardPreservingLocalFields(local, { ...card, id: listId })
      : { ...card, id: listId }
    merged = preserveNonTargetLinesScheduleOnBackendMerge(local, merged, orderId)
    if (
      local &&
      local.services.some((l) => isFollowUpCareLine(l)) &&
      local.services.some((l) => !isFollowUpCareLine(l))
    ) {
      merged = preserveMainLineOnMergedFollowUpCard(local, merged)
    }
    if (!isOrderScheduleCleared(canonicalId)) {
      merged = promoteBookedScheduleOnPendingCard(merged)
    }
    const next = purgeDuplicateOrdersById(merged, listId)
    next[listId] = merged
    ordersById.value = next
    upsertFocusOrderList(listId, merged)
    return merged
  }

  function upsertAssignedOrdersCache(order: OrderCardDTO) {
    const list = assignedOrdersCache.value
    const idx = list.findIndex((o) => o.id === order.id)
    if (idx >= 0) {
      assignedOrdersCache.value = list.map((o, i) => (i === idx ? order : o))
      return
    }
    assignedOrdersCache.value = [...list, order]
  }

  function purgeOrderFromAssignedCaches(orderId: string) {
    const target = findOrder(orderId) ?? ({ id: orderId } as OrderCardDTO)
    assignedOrdersCache.value = assignedOrdersCache.value.filter(
      (o) => !careOrderCardsSameOrder(o, target),
    )
  }

  return {
    ordersById,
    serviceCycleByOrderId,
    careMeta,
    durationLabelsByOrderId,
    planLabelsByOrderId,
    technicianNoLookup,
    findOrder,
    registerOrderCards,
    mergeOrderCardFromBackend,
    careOrderCardsSameOrder,
    careOrderIdsReferToSameOrder,
    resolveCanonicalOrderId,
    isOrderScheduleCleared,
    upsertFocusOrderList,
    upsertLocalFocusOrderCard,
    purgeDuplicateOrdersById,
    orderIdMatchesCard,
    parseOrderPrimaryKeyFromId,
    cacheServiceCycleForOrder,
    serviceCycleYmdForOrderId,
    durationLabelForOrderId,
    resolvePlanDisplayName,
    planDisplayNameForOrder,
    cachePlanLabelFromDetail,
    upsertAssignedOrdersCache,
    purgeOrderFromAssignedCaches,
  }
}
